import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";
import { useReactToPrint } from "react-to-print";
import {
  FaColumns,
  FaCompress,
  FaExpand,
  FaLayerGroup,
  FaChevronDown,
  FaChevronUp,
  FaChevronRight,
  FaChevronLeft,
} from "react-icons/fa";
import { MdOutlineSegment, MdOutlineFileDownload } from "react-icons/md";
import { IoIosArrowDown, IoMdClose } from "react-icons/io";
import {
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";

import ModifyColumns from "./Tools/ModifyColumns";
import { DateRangeSelector } from "./../styles/CustomDateRangePicker";
import {
  FilterComponent,
  renderTableCell,
  renderTableTotal,
  apiRequestBodyBuilder,
  getFilteredData,
  formatSegmentData,
  getPriorityColumnsFromStorage,
  getSortedDataGLOBAL,
} from "./config/tableHelper";
import {
  INITIAL_COLUMNS,
  USERINTENT_COLUMNS,
  VIEWBY_COLUMNS,
  GROUP_COLUMNS,
  GROUP_VIEWBY_COLUMNS,
  DEVICE_VIEWBY_COLUMNS,
  NETWORK_VIEWBY_COLUMNS,
} from "./Components/SearchTerms/SearchtermsColumns";
import { RenderTable } from "./config/tableWrapper";
import { generatePDF, generateCSV } from "./Components/DownloadFuncs";

// Skeleton styled identically to PaidAdsPage TableSkeleton
function TableSkeleton() {
  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="flex items-center gap-8 sm:gap-12">
          <div className="h-4 w-16 bg-gray-200 rounded hidden sm:block" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded hidden md:block" />
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100 animate-pulse">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
          <div key={item} className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${140 + (item % 3) * 35}px` }}
              />
            </div>
            <div className="flex items-center gap-8 sm:gap-12">
              <div className="h-4 w-14 bg-gray-100 rounded hidden sm:block" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-20 bg-gray-100 rounded hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const SearchTerms = () => {
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [columns, setColumns] = useState(INITIAL_COLUMNS);
  const [filterArray, setFilterArray] = useState([]);
  const [currentGroupType, setCurrentGroupType] = useState("");
  const [random, setRandom] = useState(0);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentSegment, setCurrentSegment] = useState("default");
  const [viewMode, setViewMode] = useState("searchterms");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [total, setTotal] = useState({});
  const [savedGroups, setSavedGroups] = useState([]);
  const [selectedGroupIds, SetSelectedGroupIds] = useState([]);
  const [tableVisible, setTableVisible] = useState(true);
  const [data, setData] = useState([]);
  const [showUserIntentMenu, setShowUserIntentMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tempUserIntentFilters, setTempUserIntentFilters] = useState({
    informational: false,
    navigational: false,
    transactional: false,
    commercial: false,
    local: false,
  });
  const [isGroupDropdownVisible, setGroupDropdownVisible] = useState(false);
  const [isUserIntentFilter, setIsUserIntentFilter] = useState(false);
  const [showViewBy, setShowViewBy] = useState(false);
  const [showDatewiseType, setShowDatewiseType] = useState(false);
  const [groupBy, setGroupBy] = useState("date");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isDownloading, setIsDownloading] = useState(false);

  let today = new Date(),
    year = today.getFullYear(),
    month = today.getMonth();
  const [state, setState] = useState([
    { startDate: new Date(year, month, 1), endDate: today, key: "selection" },
  ]);

  const datePickerRef = useRef(null);
  const columnRef = useRef();
  const groupRef = useRef();
  const segmentButtonRef = useRef(null);
  const exportRef = useRef(null);
  const downloadRef = useRef(null);

  const filteredResult = useMemo(() => {
    return getFilteredData(Array.isArray(data) ? data : [], filterArray);
  }, [data, filterArray]);

  const handleDownload = async (type) => {
    setShowDownloadMenu(false);
    setIsDownloading(true);
    try {
      const filename = `Search_Terms_Report_${format(new Date(), "yyyy-MM-dd")}.${type}`;
      const dateRangeStr =
        state[0].startDate === state[0].endDate
          ? "All time"
          : `${format(state[0].startDate, "MMM dd, yyyy")} - ${format(state[0].endDate, "MMM dd, yyyy")}`;

      // Delay slightly so UI spinner renders
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (type === "pdf") {
        await generatePDF(
          filteredResult,
          columns,
          dateRangeStr,
          filename,
          "Search Terms report",
        );
      } else if (type === "csv") {
        await generateCSV(
          filteredResult,
          columns,
          dateRangeStr,
          filename,
          "Search Terms report",
        );
      }
    } catch (error) {
      console.error(`Failed to generate ${type.toUpperCase()}:`, error);
    } finally {
      setIsDownloading(false);
    }
  };

  const getCustomColumns = async () => {
    try {
      const requestBody = apiRequestBodyBuilder(null);

      const response = await axios.post(
        "https://api.confidanto.com/custom_columns/get-custom-columns",
        requestBody,
      );
      const temp = response.data.data.map((i) => ({
        id: i.id,
        title: i.custom_column,
        key: i.custom_column,
        visible: false,
        category: "Custom Columns",
        type: "number",
      }));
      setColumns((prevColumns) => {
        const prevColsFiltered = prevColumns.filter(
          (item) => item.category !== "Custom Columns",
        );
        const allColumns = [...prevColsFiltered, ...temp];

        const sorted = getPriorityColumnsFromStorage(allColumns);

        return sorted;
      });
    } catch (error) {
      setColumns((prevColumns) => {
        const sorted = getPriorityColumnsFromStorage(prevColumns);
        return sorted;
      });
      console.error("Error fetching custom columns:", error);
    }
  };

  const fetchGroups = async () => {
    try {
      let requestBody = apiRequestBodyBuilder();

      const responseInfo = await axios.post(
        "https://api.confidanto.com/campaign-group2/group-info",
        requestBody,
      );
      let typeForQuery = "campaigns";
      if (responseInfo.data?.type) {
        const TYPEQUERY =
          responseInfo.data.type.charAt(0).toUpperCase() +
          responseInfo.data.type.slice(1);
        setCurrentGroupType(TYPEQUERY);
        typeForQuery =
          responseInfo.data.type === "keyword"
            ? "keywords"
            : responseInfo.data.type;
      }

      requestBody.type = typeForQuery;
      const response = await axios.post(
        "https://api.confidanto.com/campaign-group/fetch-groups",
        requestBody,
      );
      if (response.data.groups) {
        setSavedGroups(response.data.groups);
      }
    } catch (error) {
      console.error("Error fetching campaign groups:", error);
    }
  };

  const fetchSegmentSearchTerms = async () => {
    try {
      const requestBody = apiRequestBodyBuilder(state, {
        group_by: groupBy,
      });

      const res = await axios.post(
        "https://api.confidanto.com/searchterms/segment-data",
        requestBody,
      );
      setData(formatSegmentData(res.data, groupBy));
      setColumns(VIEWBY_COLUMNS);
    } catch (err) {
      console.error("Error: ", err);
    }
  };

  const fetchAdGroupData = async () => {
    try {
      const requestBody = apiRequestBodyBuilder(state);
      const res = await axios.post(
        "https://api.confidanto.com/searchterms/datewise-data",
        requestBody,
      );
      const segmentedData = res.data.map((item) => ({
        ...item,
        segment: categorizeSearchTerm(item.search_term),
        brand: item.search_term.toLowerCase().includes("brand")
          ? "Brand"
          : "Non-Brand",
      }));
      setData(segmentedData);
      setColumns(INITIAL_COLUMNS);
      setTotal({});
    } catch (error) {
      console.error("Error fetching ad group data:", error);
    }
  };

  const fetchSegmentGroups = async () => {
    try {
      const requestBody = apiRequestBodyBuilder(state, {
        group_by: groupBy,
        group_ids: selectedGroupIds,
      });

      const res = await axios.post(
        "https://api.confidanto.com/searchterm-group/segment-data",
        requestBody,
      );
      setData(formatSegmentData(res.data, groupBy));

      let newCols = GROUP_VIEWBY_COLUMNS.map((col) => {
        if (col.key === "group_name") {
          col.title = `${currentGroupType} Group`;
        }
        return col;
      });
      setColumns(newCols);
    } catch (err) {
      console.error("Error: ", err);
    }
  };

  const fetchDeviceSearchTerms = async () => {
    try {
      const requestBody = apiRequestBodyBuilder(state);
      const res = await axios.post(
        "https://api.confidanto.com/searchterms/device",
        requestBody,
      );
      setData(res.data);
      setColumns(DEVICE_VIEWBY_COLUMNS);
    } catch (err) {
      console.error("Error: ", err);
    }
  };

  const fetchNetworkSearchTerms = async () => {
    try {
      const requestBody = apiRequestBodyBuilder(state);
      const res = await axios.post(
        "https://api.confidanto.com/searchterms/network",
        requestBody,
      );
      setData(res.data);
      setColumns(NETWORK_VIEWBY_COLUMNS);
    } catch (err) {
      console.error("Error: ", err);
    }
  };

  const toggleCurrentSegments = (segment, from) => {
    toggleDropDownTabs("Hide all");
    if (from === "segments") {
      setGroupBy(segment);
      setCurrentSegment("segments");
    } else {
      setCurrentSegment(segment);
    }
    setRandom((p) => p + 1);
  };

  const categorizeSearchTerm = (term) => {
    const lowerTerm = term.toLowerCase();
    if (/\b(reviews|how|which|where|what|why|does|tips)\b/.test(lowerTerm))
      return "Informational Queries";
    if (/\b(blog|pricing|login|sign up)\b/.test(lowerTerm))
      return "Navigational Queries";
    if (/\b(buy|subscribe|book|reserve)\b/.test(lowerTerm))
      return "Transactional Queries";
    if (/\b(comparison|vs|versus|best|top)\b/.test(lowerTerm))
      return "Commercial Investigation Queries";
    if (/\b(near me|in|local)\b/.test(lowerTerm)) return "Local Queries";
    return "Uncategorized";
  };

  const fetchGroupData = async () => {
    setData([]);
    try {
      const requestBody = apiRequestBodyBuilder(state, {
        group_ids: selectedGroupIds,
      });

      const res = await axios.post(
        "https://api.confidanto.com/searchterm-group/datewise-data",
        requestBody,
      );
      setData(res.data);
      let newCols = GROUP_COLUMNS.map((col) => {
        if (col.key === "group_name") {
          col.title = `${currentGroupType} Group`;
        }
        return col;
      });
      setColumns(newCols);
      setTotal({});
    } catch (err) {
      console.log(err);
    }
  };

  const fetchUserIntentData = async () => {
    try {
      const activeFilters = Object.entries(tempUserIntentFilters)
        .filter(([, v]) => v)
        .map(
          ([k]) =>
            ({
              informational: "Informational Queries",
              navigational: "Navigational Queries",
              transactional: "Transactional Queries",
              commercial: "Commercial Investigation Queries",
              local: "Local Queries",
            })[k],
        )
        .filter(Boolean);
      const filtered = data.filter(
        (item) =>
          activeFilters.length === 0 || activeFilters.includes(item.segment),
      );
      const objArr = activeFilters.map((i) => {
        let totals = { impressions: 0, costs: 0, clicks: 0, conversions: 0 };
        filtered.forEach((item) => {
          if (item.segment === i) {
            for (const key in totals) totals[key] += item[key] || 0;
          }
        });
        return {
          title: i,
          ...totals,
          conversion_rate: totals.clicks
            ? (totals.conversions / totals.clicks) * 100
            : 0,
          cost_per_conv: totals.conversions
            ? totals.costs / totals.conversions
            : 0,
        };
      });
      setData(objArr);
      setColumns(USERINTENT_COLUMNS);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setCurrentPage(1);
      try {
        setLoading(true);
        setData([]);
        setFilterArray([]);
        await fetchGroups();

        switch (currentSegment) {
          case "default":
            if (viewMode === "groups") await fetchGroupData();
            else await fetchAdGroupData();
            break;
          case "segments":
            if (viewMode === "groups") await fetchSegmentGroups();
            else await fetchSegmentSearchTerms();
            break;
          case "userintent":
            await fetchUserIntentData();
            break;
          case "device":
            await fetchDeviceSearchTerms();
            break;
          case "network":
            await fetchNetworkSearchTerms();
            break;
          default:
            await fetchAdGroupData();
            break;
        }
        await getCustomColumns();
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
        if (columns.length > 0) {
          handleSort(columns[0].key);
        }
      }
    };
    fetchData();
  }, [currentSegment, random, viewMode]);

  const handleUserIntentChange = (e) => {
    const { name, checked } = e.target;
    setTempUserIntentFilters((prev) => ({ ...prev, [name]: checked }));
  };

  const toggleDropDownTabs = (type) => {
    setShowDatePicker(type === "Calendar" ? !showDatePicker : false);
    setShowUserIntentMenu(type === "Segment" ? !showUserIntentMenu : false);
    setGroupDropdownVisible(
      type === "Category" ? !isGroupDropdownVisible : false,
    );
    setShowColumnsMenu(type === "Columns" ? !showColumnsMenu : false);
    setShowDownloadMenu(type === "Download" ? !showDownloadMenu : false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideMuiMenu = event.target.closest(
        ".groupwise-select-menu",
      );
      if (
        groupRef.current &&
        !groupRef.current.contains(event.target) &&
        !isClickInsideMuiMenu
      )
        setGroupDropdownVisible(false);
      if (columnRef.current && !columnRef.current.contains(event.target))
        setShowColumnsMenu(false);
      if (
        segmentButtonRef.current &&
        !segmentButtonRef.current.contains(event.target)
      )
        setShowUserIntentMenu(false);
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      )
        setShowDatePicker(false);
      if (downloadRef.current && !downloadRef.current.contains(event.target))
        setShowDownloadMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleFullScreen = () => setIsFullScreen(!isFullScreen);

  const toggleGroupSegment = () => {
    if (viewMode === "groups") {
      setViewMode("searchterms");
      toggleDropDownTabs("Hide all");
    } else {
      toggleDropDownTabs("Category");
    }
  };

  const handleGroupChange = (event) => SetSelectedGroupIds(event.target.value);
  const getSelectedGroupNames = (selectedIds) =>
    savedGroups
      .filter((g) => selectedIds.includes(g.group_id))
      .map((g) => g.group_name)
      .join(", ");

  const handleSort = (key) =>
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));

  const removeFilterFromArray = (id) =>
    setFilterArray((prev) => prev.filter((f) => f.id !== id));

  const totalPages = Math.ceil(filteredResult.length / rowsPerPage);

  const visibleColumnsCount = columns.filter((c) => c.visible).length;

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };
  const handlePageChange = (newPage) => setCurrentPage(newPage);

  const isLockedColumn = (col) => col.isLocked || col.locked || col.sticky;
  return (
    <div ref={exportRef}>
      <div
        className={`flex h-screen bg-white ${
          isFullScreen
            ? "fixed top-0 left-0 w-full h-full z-50 overflow-x-scroll"
            : ""
        }`}
      >
        <main className="flex flex-col flex-1 w-0  overflow-auto no-scrollbar p-6">
          <div
            className={`transition-opacity duration-300 ${loading ? "pointer-events-none opacity-50" : ""}`}
          >
            <DateRangeSelector
              state={state}
              setState={setState}
              setRandom={setRandom}
              showDatePicker={showDatePicker}
              toggleDropDownTabs={toggleDropDownTabs}
              datePickerRef={datePickerRef}
              Page="Search Terms"
            />
          </div>
          <div className="flex justify-between items-center mb-4">
            <div
              className={`transition-opacity duration-300 ${loading ? "pointer-events-none opacity-50" : ""}`}
            >
              <FilterComponent
                columns={columns}
                data={data}
                filterArray={filterArray}
                setFilterArray={setFilterArray}
                segmentType={groupBy}
              />
            </div>
            <div className="flex space-x-2">
              <div className="relative" ref={segmentButtonRef}>
                <button
                  disabled={loading}
                  className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                    currentSegment !== "default"
                      ? "bg-blue-100 text-blue-600"
                      : "bg-transparent text-gray-600"
                  } ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"}`}
                  onClick={() => toggleDropDownTabs("Segment")}
                >
                  <MdOutlineSegment className="text-md" />
                  <span className="text-md">Segment</span>
                </button>
                {showUserIntentMenu && (
                  <div className="absolute z-40 w-60 bg-white shadow-lg rounded-lg mt-2 p-4 border">
                    <div
                      className="p-2 hover:bg-gray-100 capitalize cursor-pointer"
                      onClick={() => {
                        toggleCurrentSegments("default", "default");
                        setViewMode("searchterms");
                      }}
                    >
                      Reset
                    </div>
                    <p className="p-2 text-sm text-gray-400">By</p>

                    <button
                      className={`p-2 flex items-center cursor-pointer justify-between w-full ${
                        currentSegment === "userintent"
                          ? "bg-blue-100 text-blue-600"
                          : "hover:bg-gray-50"
                      }`}
                      onClick={() => setIsUserIntentFilter(!isUserIntentFilter)}
                    >
                      User Intent{" "}
                      <IoIosArrowDown
                        className={`ml-2 transform transition-transform duration-300 ${
                          isUserIntentFilter ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {isUserIntentFilter && (
                      <div className="px-4 py-2">
                        <div className="flex flex-col space-y-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              name="informational"
                              checked={tempUserIntentFilters.informational}
                              onChange={handleUserIntentChange}
                              className="mr-2"
                            />
                            Informational
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              name="navigational"
                              checked={tempUserIntentFilters.navigational}
                              onChange={handleUserIntentChange}
                              className="mr-2"
                            />
                            Navigational
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              name="transactional"
                              checked={tempUserIntentFilters.transactional}
                              onChange={handleUserIntentChange}
                              className="mr-2"
                            />
                            Transactional
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              name="commercial"
                              checked={tempUserIntentFilters.commercial}
                              onChange={handleUserIntentChange}
                              className="mr-2"
                            />
                            Commercial
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              name="local"
                              checked={tempUserIntentFilters.local}
                              onChange={handleUserIntentChange}
                              className="mr-2"
                            />
                            Local
                          </label>
                        </div>
                        <button
                          type="button"
                          disabled={
                            !Object.values(tempUserIntentFilters).some(Boolean)
                          }
                          onClick={() =>
                            toggleCurrentSegments("userintent", "default")
                          }
                          className="bg-blue-500 text-white px-4 py-2 rounded mt-2 hover:bg-blue-600 w-full disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-500"
                        >
                          Apply
                        </button>
                      </div>
                    )}

                    <button
                      className="p-2 flex items-center hover:bg-gray-50 cursor-pointer w-full justify-between"
                      onClick={() => setShowViewBy(!showViewBy)}
                    >
                      View By{" "}
                      <IoIosArrowDown
                        className={`ml-2 transform transition-transform duration-300 ${
                          showViewBy ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {showViewBy && (
                      <ul className="mx-2">
                        <li>
                          <div className="relative w-full cursor-pointer">
                            <div
                              className="w-full text-left hover:bg-slate-50 h-10 p-2 rounded-sm flex justify-between items-center"
                              onClick={() =>
                                setShowDatewiseType(!showDatewiseType)
                              }
                            >
                              <h2>Date</h2>
                              <IoIosArrowDown
                                className={`transition-transform duration-300 ${
                                  showDatewiseType ? "rotate-180" : ""
                                }`}
                              />
                            </div>
                            {showDatewiseType && (
                              <div className="pl-3 mt-0.5 flex flex-col space-y-1">
                                <li
                                  className={`p-2 capitalize cursor-pointer ${
                                    currentSegment === "segments" &&
                                    groupBy === "date"
                                      ? "bg-blue-100 text-blue-600"
                                      : "hover:bg-gray-100"
                                  }`}
                                  onClick={() =>
                                    toggleCurrentSegments("date", "segments")
                                  }
                                >
                                  Day
                                </li>
                                <li
                                  className={`p-2 capitalize cursor-pointer ${
                                    currentSegment === "segments" &&
                                    groupBy === "week"
                                      ? "bg-blue-100 text-blue-600"
                                      : "hover:bg-gray-100"
                                  }`}
                                  onClick={() =>
                                    toggleCurrentSegments("week", "segments")
                                  }
                                >
                                  Week
                                </li>
                                <li
                                  className={`p-2 capitalize cursor-pointer ${
                                    currentSegment === "segments" &&
                                    groupBy === "month"
                                      ? "bg-blue-100 text-blue-600"
                                      : "hover:bg-gray-100"
                                  }`}
                                  onClick={() =>
                                    toggleCurrentSegments("month", "segments")
                                  }
                                >
                                  Month
                                </li>
                              </div>
                            )}
                          </div>
                        </li>
                        <li
                          className={`p-2 capitalize cursor-pointer ${
                            currentSegment === "network"
                              ? "bg-blue-100 text-blue-600"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() =>
                            toggleCurrentSegments("network", "network")
                          }
                        >
                          Network
                        </li>
                        <li
                          className={`p-2 capitalize cursor-pointer ${
                            currentSegment === "device"
                              ? "bg-blue-100 text-blue-600"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() =>
                            toggleCurrentSegments("device", "device")
                          }
                        >
                          Device
                        </li>
                      </ul>
                    )}
                  </div>
                )}
              </div>
              <div className="relative" ref={groupRef}>
                <button
                  disabled={loading}
                  className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                    loading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-100"
                  } bg-transparent text-gray-600`}
                  onClick={toggleGroupSegment}
                >
                  <FaLayerGroup className="text-md" />
                  <span className="text-md">
                    {viewMode === "groups" ? "Search Terms" : "Groups"}
                  </span>
                </button>
                {isGroupDropdownVisible && (
                  <div className="absolute right-0 bg-white shadow-md rounded p-4 mt-2 z-40 border w-64 space-y-2">
                    {savedGroups && savedGroups.length > 0 ? (
                      <>
                        <FormControl fullWidth>
                          <InputLabel>{`${currentGroupType} Group`}</InputLabel>
                          <Select
                            multiple
                            value={selectedGroupIds}
                            onChange={handleGroupChange}
                            input={
                              <OutlinedInput
                                label={`${currentGroupType} Group`}
                              />
                            }
                            renderValue={getSelectedGroupNames}
                            MenuProps={{
                              className: "groupwise-select-menu",
                            }}
                          >
                            {savedGroups.map((data) => (
                              <MenuItem
                                key={data.group_id}
                                value={data.group_id}
                              >
                                <Checkbox
                                  checked={selectedGroupIds.includes(
                                    data.group_id,
                                  )}
                                />
                                <ListItemText primary={data.group_name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={!selectedGroupIds?.length}
                          onClick={() => {
                            setViewMode("groups");
                            setCurrentSegment("default");
                            toggleDropDownTabs("Hide all");
                          }}
                          className="mt-2"
                        >
                          Apply
                        </Button>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center p-6 text-center">
                        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                          <FaLayerGroup className="text-gray-400 text-lg" />
                        </div>

                        <p className="text-sm font-medium text-gray-700">
                          No groups available
                        </p>

                        <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                          You don’t have any groups created yet. Please create a
                          group to manage and apply group-based ads.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div
                className={`relative transition-opacity duration-300 ${loading ? "pointer-events-none opacity-50" : ""}`}
                ref={downloadRef}
              >
                <button
                  disabled={loading || isDownloading}
                  className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                    loading || isDownloading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-100"
                  } ${showDownloadMenu ? "bg-blue-100 text-blue-600" : "bg-transparent text-gray-600"}`}
                  onClick={() => toggleDropDownTabs("Download")}
                >
                  {isDownloading ? (
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <MdOutlineFileDownload className="text-md" />
                  )}
                  <span className="text-md">
                    {isDownloading ? "Downloading" : "Download"}
                  </span>
                </button>
                {showDownloadMenu && (
                  <div className="absolute right-0 z-40 w-36 bg-white shadow-lg rounded-lg mt-2 p-2 border">
                    <div
                      className="p-2 hover:bg-gray-100 cursor-pointer rounded flex items-center justify-between text-gray-700"
                      onClick={() => handleDownload("pdf")}
                    >
                      .PDF
                    </div>
                    <div
                      className="p-2 hover:bg-gray-100 cursor-pointer rounded flex items-center justify-between text-gray-700"
                      onClick={() => handleDownload("csv")}
                    >
                      .CSV
                    </div>
                  </div>
                )}
              </div>

              <button
                disabled={loading}
                className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                  isFullScreen
                    ? "bg-blue-100 text-blue-600"
                    : "bg-transparent text-gray-600"
                } ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"}`}
                onClick={toggleFullScreen}
              >
                {isFullScreen ? (
                  <FaCompress className="text-md" />
                ) : (
                  <FaExpand className="text-md" />
                )}
                <span className="text-md">
                  {isFullScreen ? "Collapse" : "Expand"}
                </span>
              </button>
              <div className="relative" ref={columnRef}>
                <button
                  disabled={loading}
                  className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                    loading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-100"
                  } bg-transparent text-gray-600`}
                  onClick={() => toggleDropDownTabs("Columns")}
                >
                  <FaColumns className="text-md" />
                  <span className="text-md">Columns</span>
                </button>
                {showColumnsMenu && (
                  <ModifyColumns
                    columns={columns}
                    setColumns={setColumns}
                    setTableVisible={setTableVisible}
                    setShowColumnsMenu={setShowColumnsMenu}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="">
            {filterArray.length > 0 && (
              <div className="w-full flex flex-row space-x-4 items-center p-2 relative">
                <div className="flex flex-row w-11/12 overflow-auto scroll-container">
                  {filterArray.map((item) => (
                    <div
                      key={item.id}
                      className="flex flex-row items-center justify-between p-2 border border-black rounded-lg min-w-fit mx-1"
                    >
                      <h2 className="mr-2">
                        {item.title} {item.sign}{" "}
                        {Array.isArray(item.value)
                          ? item.value.join(", ")
                          : item.value}
                      </h2>
                      <button onClick={() => removeFilterFromArray(item.id)}>
                        <IoMdClose />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="clearfilter flex item-center justify-center absolute right-5 text-2xl hover:bg-red-500 hover:text-white">
                  <button onClick={() => setFilterArray([])}>
                    <IoMdClose className="ml-5" />
                  </button>
                </div>
              </div>
            )}
            <div className="overflow-x-auto w-full">
              {loading ? (
                <TableSkeleton />
              ) : filteredResult.length > 0 ? (
                <RenderTable
                  columns={columns}
                  data={filteredResult}
                  currentPage={currentPage}
                  rowsPerPage={rowsPerPage}
                  variant="performance"
                  enableExpandCollapse={state.length > 1}
                />
              ) : (
                <div className="flex justify-center items-center h-40 mt-8 text-gray-600">
                  No data found
                </div>
              )}
            </div>
          </div>
          {!loading && filteredResult.length > 0 ? (
            <div className="flex justify-end items-center mt-4 font-serif p-3 rounded-lg">
              <div className="flex items-center space-x-2 mr-4">
                <label className="text-gray-700">Rows per page:</label>
                <select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  className="border rounded px-2 py-1"
                >
                  <option>5</option>
                  <option>10</option>
                  <option>20</option>
                  <option>50</option>
                  <option>100</option>
                  <option>200</option>
                </select>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="px-1 py-1 rounded disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <FaChevronLeft />
                </button>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                  className="px-1 py-1 font-bold rounded disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          ) : null}
        </main>
      </div>
    </div>
  );
};

export default SearchTerms;
